import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'latlens-theme';

function resolveSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function storedTheme(): Theme {
  try {
    return (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? 'system';
  } catch {
    return 'system';
  }
}

function applyTheme(theme: Theme) {
  const resolved = theme === 'system' ? resolveSystemTheme() : theme;
  document.documentElement.classList.toggle('light', resolved === 'light');
}

/** Called once at startup, so every page (sign-in included) follows the setting. */
export function applyStoredTheme() {
  applyTheme(storedTheme());
}

export function useTheme() {
  // Follows the device by default, like latlens.com and the maps app.
  const [theme, setThemeState] = useState<Theme>(() => (typeof window === 'undefined' ? 'system' : storedTheme()));

  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* storage blocked */ }
    setThemeState(next);
  }, []);

  return { theme, setTheme };
}
